/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { supabase } from '@/lib/supabase'
import type { Profile, CreateProfileInput, UpdateProfileInput } from '@/types/workspace'

// =============================================
// PROFILE API FUNCTIONS
// =============================================

/**
 * Get a profile by user ID
 */
export async function getProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle()

  if (error) {
    console.error('Error fetching profile:', error)
    return null
  }

  return data
}

/**
 * Get current user's profile
 */
export async function getCurrentProfile(): Promise<Profile | null> {
  const { data: { user } } = await supabase.auth.getUser()

  if (!user) {
    return null
  }

  return getProfile(user.id)
}

/**
 * Create a profile for the current user
 */
export async function createProfile(input: CreateProfileInput): Promise<Profile> {
  const { data: { user } } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('User not authenticated')
  }

  const { data, error } = await supabase
    .from('profiles')
    .insert({
      id: user.id,
      email: user.email || '',
      ...input,
    })
    .select()
    .maybeSingle()

  if (error) {
    console.error('Error creating profile:', error)
    throw error
  }

  if (!data) {
    throw new Error('Failed to create profile')
  }

  return data
}

/**
 * Update current user's profile
 */
export async function updateProfile(input: UpdateProfileInput): Promise<Profile> {
  const { data: { user } } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('User not authenticated')
  }

  // Use upsert to either update existing profile or create new one
  const { data, error } = await supabase
    .from('profiles')
    .upsert({
      id: user.id,
      email: user.email || '',
      ...input,
    })
    .select()
    .maybeSingle()

  if (error) {
    console.error('Error updating profile:', error)
    throw error
  }

  if (!data) {
    throw new Error('Failed to update profile')
  }

  return data
}

/**
 * Upload avatar image to Supabase Storage
 */
export async function uploadAvatar(file: File): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('User not authenticated')
  }

  // Create a unique file name
  const fileExt = file.name.split('.').pop()
  const fileName = `${user.id}-${Date.now()}.${fileExt}`
  const filePath = `avatars/${fileName}`

  // Upload the file
  const { error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: true,
    })

  if (uploadError) {
    console.error('Error uploading avatar:', uploadError)
    throw uploadError
  }

  // Get the public URL
  const { data: { publicUrl } } = supabase.storage
    .from('avatars')
    .getPublicUrl(filePath)

  return publicUrl
}

/**
 * Delete avatar image from Supabase Storage
 */
export async function deleteAvatar(avatarUrl: string): Promise<void> {
  // Extract the file path from the URL
  const urlParts = avatarUrl.split('/avatars/')
  if (urlParts.length < 2) {
    return
  }

  const filePath = `avatars/${urlParts[1]}`

  const { error } = await supabase.storage
    .from('avatars')
    .remove([filePath])

  if (error) {
    console.error('Error deleting avatar:', error)
    throw error
  }
}

/**
 * Get multiple profiles by user IDs
 */
export async function getProfiles(userIds: string[]): Promise<Profile[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .in('id', userIds)

  if (error) {
    console.error('Error fetching profiles:', error)
    throw error
  }

  return data || []
}
